import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const targets =
  ':scope > div, :scope > section, :scope > form, :scope > article, h1, h2, h3, legend, .gp-cta, [role="status"], [role="alert"], [data-gp-media], [data-gp-reveal], [data-gp-stagger] > *';
type MotionElement = HTMLElement | SVGElement;

/** Animate the current DOM only. Never keep an outgoing route or a copy of its private content. */
export function usePageMotion<T extends HTMLElement = HTMLElement>() {
  const root = useRef<T>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const scene = root.current;
    if (!scene || typeof IntersectionObserver === 'undefined') return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const seen = new WeakSet<Element>();
    const pending = new Set<MotionElement>();
    const running = new Map<Animation, MotionElement>();
    const cancel = () => {
      running.forEach((_, animation) => animation.cancel());
      running.clear();
    };
    const visible = () =>
      !preference.matches && document.visibilityState === 'visible';
    const track = (animation: Animation, element: MotionElement) => {
      running.set(animation, element);
      const release = () => running.delete(animation);
      void animation.finished.then(release, release);
    };
    const reveal = (element: MotionElement) => {
      if (
        !visible() ||
        !element.isConnected ||
        element.contains(document.activeElement) ||
        typeof element.animate !== 'function'
      )
        return;
      // Page roots only fade: transforming them would move fixed/sticky descendants.
      const pageRoot = element.parentElement === scene;
      const message = element.dataset.gpReveal === 'fade';
      const feedback = element.matches('[role="status"], [role="alert"]');
      const media = element.hasAttribute('data-gp-media');
      const sparkle =
        element.dataset.gpReveal === 'sparkle' &&
        element.getAttribute('aria-hidden') === 'true';
      // Only decorative icons may bloom; logos, text and control hit areas stay still.
      const icon =
        element instanceof SVGElement &&
        element.dataset.gpReveal === 'icon' &&
        element.getAttribute('aria-hidden') === 'true';
      const halo =
        element.dataset.gpReveal === 'halo' &&
        element.classList.contains('gp-motion-halo') &&
        element.getAttribute('aria-hidden') === 'true';
      const shine = element.classList.contains('gp-cta');
      if (
        shine &&
        (element.matches(':disabled') ||
          element.getAttribute('aria-disabled') === 'true')
      )
        return;
      // A pointer must not move away from a checkbox/field when focus cancels an entrance.
      const interactive =
        element.matches('a, button, form, input, textarea, select') ||
        !!element.closest('a, button, summary, [role="button"]') ||
        !!element.querySelector(
          'a, button, input, textarea, select, [tabindex]',
        );
      const fadeOnly = pageRoot || message || feedback || interactive;
      const heading = element.matches('h1, h2, h3, legend');
      const item =
        icon || halo || shine
          ? (element.closest('[data-gp-stagger] > *') ?? element)
          : element;
      const stagger = item.parentElement?.hasAttribute('data-gp-stagger');
      const index = stagger
        ? Array.from(item.parentElement!.children).indexOf(item)
        : 0;
      const offset = pageRoot ? 0 : heading ? 6 : 12;
      const animation = element.animate(
        sparkle
          ? [
              { opacity: 0.15, transform: 'translateY(16px) scale(0.65)' },
              {
                opacity: 0.8,
                transform: 'translateY(-8px) scale(1.1)',
                offset: 0.45,
              },
              { opacity: 0.35, transform: 'none' },
            ]
          : media
            ? [
                { opacity: 0.65, clipPath: 'inset(0 0 8% 0)' },
                { opacity: 1, clipPath: 'inset(0)' },
              ]
            : shine
              ? [
                  { backgroundPosition: '100% 0' },
                  { backgroundPosition: '0 0' },
                ]
              : halo
                ? [
                    { opacity: 0, transform: 'scale(0.55)' },
                    { opacity: 0.65, transform: 'scale(1.16)', offset: 0.65 },
                    { opacity: 0.35, transform: 'none' },
                  ]
                : icon
                  ? [
                      { opacity: 0.5, transform: 'scale(0.72) rotate(-12deg)' },
                      {
                        opacity: 1,
                        transform: 'scale(1.08) rotate(3deg)',
                        offset: 0.7,
                      },
                      { opacity: 1, transform: 'none' },
                    ]
                  : fadeOnly
                    ? [{ opacity: 0.65 }, { opacity: 1 }]
                    : [
                        { opacity: 0.2, transform: `translateY(${offset}px)` },
                        { opacity: 1, transform: 'none' },
                      ],
        {
          duration: sparkle
            ? 2400
            : media
              ? 900
              : feedback
                ? 320
                : shine
                  ? 850
                  : halo
                    ? 820
                    : icon
                      ? 640
                      : pageRoot
                        ? 260
                        : message
                          ? 180
                          : 480,
          delay: sparkle
            ? Array.from(element.parentElement!.children).indexOf(element) * 100
            : Math.min(index, 5) * 45 + (icon || halo ? 60 : shine ? 140 : 0),
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          // Content is visible by default; failure/cancellation never leaves it hidden.
          fill: 'none',
        },
      );
      track(animation, element);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          observer.unobserve(target);
          pending.delete(target as MotionElement);
          reveal(target as MotionElement);
        });
      },
      { threshold: 0.08 },
    );
    const scan = () => {
      scene.querySelectorAll<MotionElement>(targets).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        if (element.matches('.sr-only, [hidden]')) return;
        if (element.classList.contains('gp-hero-title')) return;
        // Explicit sections own their headings; avoid two simultaneous transforms.
        if (
          element.tagName === 'H1' &&
          element.closest('[data-gp-reveal], [data-gp-stagger]')
        )
          return;
        if (visible()) {
          pending.add(element);
          observer.observe(element);
        }
      });
      pending.forEach((element) => {
        if (!scene.contains(element)) {
          observer.unobserve(element);
          pending.delete(element);
        }
      });
      // Removed private nodes must also leave the animation bookkeeping immediately.
      running.forEach((element, animation) => {
        if (!scene.contains(element)) {
          animation.cancel();
          running.delete(animation);
        }
      });
    };
    const suspend = () => {
      observer.disconnect();
      pending.clear();
      cancel();
    };
    const boundaries = () => {
      if (!visible()) suspend();
    };
    const focus = (event: FocusEvent) => {
      running.forEach((element, animation) => {
        if (event.target instanceof Node && element.contains(event.target)) {
          animation.cancel();
          running.delete(animation);
        }
      });
    };
    const press = (event: MouseEvent) => {
      const element =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(
              'button, summary, a.gp-control, a.gp-card',
            )
          : null;
      if (
        !element ||
        !scene.contains(element) ||
        !visible() ||
        element.closest(':disabled, [aria-disabled="true"], [inert]') ||
        typeof element.animate !== 'function'
      )
        return;
      // Feedback never moves a control, delays its action, or retains any input/content.
      running.forEach((target, animation) => {
        if (target === element && animation.id === 'gp-press-feedback') {
          animation.cancel();
          running.delete(animation);
        }
      });
      const shadow = getComputedStyle(element).boxShadow;
      const animation = element.animate(
        [
          { boxShadow: shadow },
          {
            boxShadow:
              '0 0 0 6px color-mix(in srgb, var(--color-gp-pink-500) 25%, transparent)',
            offset: 0.25,
          },
          { boxShadow: shadow },
        ],
        { duration: 520, easing: 'ease-out', fill: 'none' },
      );
      animation.id = 'gp-press-feedback';
      track(animation, element);
    };
    const mutations = new MutationObserver((records) => {
      // Explicit results may reuse their node. Timers/counters do not replay on every tick.
      const updates = new Set<MotionElement>();
      records.forEach(({ target }) => {
        const element =
          target instanceof Element ? target : target.parentElement;
        const feedback = element?.closest<MotionElement>(
          '[role="alert"], [role="status"][data-gp-feedback]',
        );
        if (
          feedback &&
          seen.has(feedback) &&
          !pending.has(feedback) &&
          !feedback.matches('.sr-only, [hidden]') &&
          scene.contains(feedback)
        )
          updates.add(feedback);
      });
      scan();
      updates.forEach((element) => {
        running.forEach((target, animation) => {
          if (target === element) {
            animation.cancel();
            running.delete(animation);
          }
        });
        reveal(element);
      });
    });
    mutations.observe(scene, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    scan();
    preference.addEventListener('change', boundaries);
    document.addEventListener('visibilitychange', boundaries);
    window.addEventListener('pagehide', suspend);
    window.addEventListener('offline', suspend);
    scene.addEventListener('focusin', focus);
    scene.addEventListener('click', press);
    return () => {
      observer.disconnect();
      pending.clear();
      mutations.disconnect();
      cancel();
      preference.removeEventListener('change', boundaries);
      document.removeEventListener('visibilitychange', boundaries);
      window.removeEventListener('pagehide', suspend);
      window.removeEventListener('offline', suspend);
      scene.removeEventListener('focusin', focus);
      scene.removeEventListener('click', press);
    };
  }, [pathname]);

  return root;
}
